import React, { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import './Ads.css';

// ============================================================================
// Google AdSense (REVAMP).
//
// The approved publisher ID and ad-unit slot IDs below are public identifiers
// (they appear in the page source of every site running AdSense), so they ship
// in the bundle as defaults; REACT_APP_ADSENSE_* env vars still override them.
// Live placements: 2 side rails, homepage top/bottom (leaderboard), module
// top/bottom (leaderboard, reusing the homepage display units), processing
// banner (leaderboard), interstitial.
//
// Setup: see docs/adsense_setup.md.
//   REACT_APP_ADSENSE_CLIENT=ca-pub-XXXXXXXXXXXXXXXX
//   REACT_APP_ADSENSE_SLOT_BANNER=XXXXXXXXXX
//   REACT_APP_ADSENSE_SLOT_INTERSTITIAL=XXXXXXXXXX
// ============================================================================

const DEFAULT_ADSENSE_CLIENT = 'ca-pub-8542852505756382';
const DEFAULT_SLOT_RAIL_LEFT = '3853707376';  // AdSense ad unit "banner — left-rail"
const DEFAULT_SLOT_RAIL_RIGHT = '3198372307'; // AdSense ad unit "Ad banner — right-rail"
const DEFAULT_SLOT_HOMEPAGE_TOP = '4049148490'; // AdSense ad unit "Ad banner — homepage-top"
const DEFAULT_SLOT_HOMEPAGE_BOTTOM = '7600651746'; // AdSense ad unit "Ad banner — homepage-bottom"
// NOTE: the old module-top / module-bottom units (5636833385 / 5441392261) are
// Multiplex grids whose height Google controls — far too tall for banners.
// Module pages therefore reuse the homepage DISPLAY units as slim fixed-size
// leaderboards (one ad unit may be reused across pages; reporting merges).
// For dedicated units: create two "Display ads" units in the AdSense console
// and set REACT_APP_ADSENSE_SLOT_MODULE_TOP / _BOTTOM (or hardcode them here).
const DEFAULT_SLOT_PROCESSING = '4782916716'; // AdSense ad unit "processing"
const DEFAULT_SLOT_INTERSTITIAL = '8530590038'; // AdSense ad unit "Interstitial"

export const ADSENSE_CLIENT = (process.env.REACT_APP_ADSENSE_CLIENT || DEFAULT_ADSENSE_CLIENT).trim();
const SLOT_BANNER = (process.env.REACT_APP_ADSENSE_SLOT_BANNER || '').trim();
// Side-rail skyscrapers. REACT_APP_ADSENSE_SLOT_VERTICAL overrides both rails at
// once; REACT_APP_ADSENSE_SLOT_RAIL_LEFT / _RIGHT override per side.
const SLOT_VERTICAL = (process.env.REACT_APP_ADSENSE_SLOT_VERTICAL || '').trim();
const SLOT_RAIL_LEFT = (process.env.REACT_APP_ADSENSE_SLOT_RAIL_LEFT || '').trim() || SLOT_VERTICAL || DEFAULT_SLOT_RAIL_LEFT;
const SLOT_RAIL_RIGHT = (process.env.REACT_APP_ADSENSE_SLOT_RAIL_RIGHT || '').trim() || SLOT_VERTICAL || DEFAULT_SLOT_RAIL_RIGHT;
const SLOT_INTERSTITIAL =
  (process.env.REACT_APP_ADSENSE_SLOT_INTERSTITIAL || DEFAULT_SLOT_INTERSTITIAL).trim();
// Module-list ("homepage") top banner; REACT_APP_ADSENSE_SLOT_HOMEPAGE_TOP overrides.
export const SLOT_HOMEPAGE_TOP =
  (process.env.REACT_APP_ADSENSE_SLOT_HOMEPAGE_TOP || DEFAULT_SLOT_HOMEPAGE_TOP).trim();
// Module-list ("homepage") bottom banner; REACT_APP_ADSENSE_SLOT_HOMEPAGE_BOTTOM overrides.
export const SLOT_HOMEPAGE_BOTTOM =
  (process.env.REACT_APP_ADSENSE_SLOT_HOMEPAGE_BOTTOM || DEFAULT_SLOT_HOMEPAGE_BOTTOM).trim();
// Module pages' top banner — reuses the homepage-top DISPLAY unit (see NOTE
// above); REACT_APP_ADSENSE_SLOT_MODULE_TOP overrides with a dedicated unit.
export const SLOT_MODULE_TOP =
  (process.env.REACT_APP_ADSENSE_SLOT_MODULE_TOP || DEFAULT_SLOT_HOMEPAGE_TOP).trim();
// Module pages' bottom banner — reuses the homepage-bottom DISPLAY unit.
export const SLOT_MODULE_BOTTOM =
  (process.env.REACT_APP_ADSENSE_SLOT_MODULE_BOTTOM || DEFAULT_SLOT_HOMEPAGE_BOTTOM).trim();
// Banner shown while an AI generation is running; REACT_APP_ADSENSE_SLOT_PROCESSING overrides.
const SLOT_PROCESSING =
  (process.env.REACT_APP_ADSENSE_SLOT_PROCESSING || DEFAULT_SLOT_PROCESSING).trim();
export const ADSENSE_ENABLED = ADSENSE_CLIENT.startsWith('ca-pub-');
const IS_DEV = process.env.NODE_ENV !== 'production';

// Frequency cap for the interstitial so it never harms UX or violates policy:
// show at most once every MIN_INTERVAL_MS, and only after every Nth trigger.
const MIN_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const EVERY_NTH_TRIGGER = 3;            // ~every 3rd generation
let triggerCount = 0;
let lastShownAt = 0;

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

function useAdScript() {
  useEffect(() => {
    if (!ADSENSE_ENABLED) return;
    const src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
    if (document.querySelector(`script[src="${src}"]`)) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = src;
    s.crossOrigin = 'anonymous';
    document.head.appendChild(s);
  }, []);
}

/**
 * A display banner. Responsive by default; pass `width`/`height` for a fixed
 * unit (e.g. 160×600 skyscraper). `slot` falls back to the env banner slot.
 */
export const AdBanner: React.FC<{
  slot?: string;
  style?: React.CSSProperties;
  label?: string;
  width?: number;
  height?: number;
  /** Ad unit format: 'auto' (responsive display) or 'autorelaxed' (Multiplex). */
  format?: 'auto' | 'autorelaxed';
  /**
   * Render as a fixed-size leaderboard (728×90 desktop / 320×100 mobile, via
   * CSS media queries) instead of a fluid responsive unit. Uses Google's
   * documented custom-sizing pattern, so the banner never grows taller.
   */
  leaderboard?: boolean;
}> = ({ slot, style, label, width, height, format = 'auto', leaderboard = false }) => {
  useAdScript();
  const insRef = useRef<HTMLModElement>(null);
  const adSlot = slot || SLOT_BANNER;
  const fixed = typeof width === 'number' && typeof height === 'number';

  useEffect(() => {
    if (!ADSENSE_ENABLED || !adSlot) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* AdSense may not be ready yet; harmless */
    }
  }, [adSlot]);

  // No publisher configured: dev placeholder, production renders nothing.
  if (!ADSENSE_ENABLED) {
    if (!IS_DEV) return null;
    return (
      <div
        className="ad-placeholder"
        style={{ minHeight: fixed ? height : 90, width: fixed ? width : undefined, ...style }}
        aria-hidden="true"
      >
        Ad banner{label ? ` — ${label}` : ''} (disabled until REACT_APP_ADSENSE_CLIENT is set)
      </div>
    );
  }
  if (!adSlot) return null;

  return (
    <div className={`ad-banner-slot${leaderboard ? ' ad-leaderboard' : ''}`} style={style}>
      {leaderboard ? (
        // Custom-sized responsive unit (Google-documented pattern): exact
        // dimensions come from CSS media queries; no data-ad-format, so
        // Google serves a true leaderboard instead of a tall fluid ad.
        <ins
          ref={insRef}
          className="adsbygoogle ad-leaderboard-ins"
          style={{ display: 'inline-block' }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={adSlot}
        />
      ) : fixed ? (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'inline-block', width, height }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={adSlot}
        />
      ) : (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block', width: '100%', ...style }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={adSlot}
          data-ad-format={format}
          data-full-width-responsive={format === 'autorelaxed' ? undefined : 'true'}
        />
      )}
    </div>
  );
};

/**
 * Fixed skyscraper rails pinned to the left & right screen edges on module
 * pages. They are position:fixed OVERLAYS (never part of the content flow, so
 * module content keeps its full width and is never compressed).
 *
 * Size adapts to the gutter available next to the centred 1200px content:
 *   • ≥1600px: 160×600 wide skyscraper
 *   • 1480–1599px: 120×600 skyscraper (e.g. 14"/16" MacBook scaled displays)
 *   • <1480px: hidden by CSS (tablets/phones — no room without overlap)
 * The `key` remounts the <ins> if the chosen size crosses a breakpoint.
 */
export const AdSideRails: React.FC = () => {
  const [wide, setWide] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1600 : true
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1600px)');
    const update = () => setWide(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  const w = wide ? 160 : 120;

  return (
    <>
      <aside className="ad-side-rail ad-side-rail-left">
        <AdBanner key={`l-${w}`} slot={SLOT_RAIL_LEFT} label="left-rail" width={w} height={600} />
      </aside>
      <aside className="ad-side-rail ad-side-rail-right">
        <AdBanner key={`r-${w}`} slot={SLOT_RAIL_RIGHT} label="right-rail" width={w} height={600} />
      </aside>
    </>
  );
};

/**
 * Centred in-content banner shown ABOVE a progress bar while an AI generation
 * is running. `active` mounts/unmounts it, so an ad request is only made during
 * actual processing (exposure without cluttering the normal UI).
 */
export const AdProcessingBanner: React.FC<{ active: boolean }> = ({ active }) => {
  if (!active) return null;
  return (
    <div className="ad-processing">
      <span className="ad-processing-label">Advertisement</span>
      <AdBanner slot={SLOT_PROCESSING} label="processing" leaderboard style={{ minHeight: 90 }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dismissible interstitial (frequency-capped). Policy-compliant: it is easy to
// close, does not force a click, and never blocks the actual result.
// ---------------------------------------------------------------------------
interface AdsContextValue {
  /** Call after a successful generation; shows the interstitial only if caps allow. */
  showInterstitial: () => void;
}
const AdsContext = createContext<AdsContextValue>({ showInterstitial: () => {} });
export const useAds = () => useContext(AdsContext);

export const AdsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);

  const showInterstitial = useCallback(() => {
    if (!ADSENSE_ENABLED || !SLOT_INTERSTITIAL) return;
    triggerCount += 1;
    const now = Date.now();
    if (triggerCount % EVERY_NTH_TRIGGER !== 0) return;
    if (now - lastShownAt < MIN_INTERVAL_MS) return;
    lastShownAt = now;
    setOpen(true);
  }, []);

  // Fire the (capped) interstitial after successful AI generations, signalled via
  // a window event dispatched by the API fetch guard (works across all modules).
  useEffect(() => {
    if (!ADSENSE_ENABLED || !SLOT_INTERSTITIAL) return;
    const handler = () => showInterstitial();
    window.addEventListener('studymind:generation-done', handler);
    return () => window.removeEventListener('studymind:generation-done', handler);
  }, [showInterstitial]);

  useEffect(() => {
    if (open) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        /* ignore */
      }
    }
  }, [open]);

  return (
    <AdsContext.Provider value={{ showInterstitial }}>
      {children}
      {open && ADSENSE_ENABLED && SLOT_INTERSTITIAL && (
        <div className="ad-interstitial-overlay" role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
          <div className="ad-interstitial" onClick={(e) => e.stopPropagation()}>
            <div className="ad-interstitial-head">
              <span className="ad-interstitial-label">Advertisement</span>
              <button type="button" className="ad-interstitial-close" aria-label="Close ad" onClick={() => setOpen(false)}>
                ✕
              </button>
            </div>
            <div className="ad-interstitial-body">
              <ins
                className="adsbygoogle"
                style={{ display: 'block', width: '100%' }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={SLOT_INTERSTITIAL}
                data-ad-format="auto"
                data-full-width-responsive="false"
              />
            </div>
            <button type="button" className="ad-interstitial-continue" onClick={() => setOpen(false)}>
              Continue to my result
            </button>
          </div>
        </div>
      )}
    </AdsContext.Provider>
  );
};
